import { Button } from "./main-landing/ui/button";
import {
  ArrowRight,
  MessageCircle,
  Phone,
  Zap,
  Clock,
  Calendar,
  Lightbulb,
  TrendingUp,
  Bell,
  Target,
  Users,
  Activity,
  X,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function HeroSection() {
  const navigate = useNavigate();
  const [animationStarted, setAnimationStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const timeString = `${hours}:${minutes.toString().padStart(2, "0")}`;
      setCurrentTime(timeString);
    };

    updateTime();
    const timeInterval = setInterval(updateTime, 60000); // Update every minute

    const timer = setTimeout(() => setAnimationStarted(true), 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(timeInterval);
    };
  }, []);

  const popoutElements = [
    // ZONE 3 - OUTSIDE PHONE (3 elements)
    {
      icon: MessageCircle,
      title: "Morning Check-in",
      content: "How are you feeling today?",
      type: "message",
      color: "#3B82F6",
      delay: 1200,
      position: { top: "15%", left: "-30%", rotate: -8 },
      size: "large",
    },
    {
      icon: Lightbulb,
      title: "Inspiration Score",
      content: "7.2/10 ↗ +0.3",
      type: "score",
      color: "#EF4444",
      delay: 1600,
      position: { top: "40%", left: "-25%", rotate: -12 },
      size: "large",
    },
    {
      icon: TrendingUp,
      title: "Progress",
      content: "Weekly +15%",
      type: "insight",
      color: "#06B6D4",
      delay: 2200,
      position: { bottom: "10%", left: "-30%", rotate: 6 },
      size: "large",
    },

    // ZONE 2 - BORDER/EDGE (3 elements)
    {
      icon: Calendar,
      title: "Today",
      content: "3 meetings scheduled",
      type: "data",
      color: "#10B981",
      delay: 1400,
      position: { top: "15%", right: "-27%", rotate: 8 },
      size: "medium",
    },
    {
      icon: Bell,
      title: "Perfect Timing",
      content: "Reminder set",
      type: "alert",
      color: "#EC4899",
      delay: 2400,
      position: { top: "60%", left: "0%", rotate: -15 },
      size: "medium",
    },
    {
      icon: Phone,
      title: "Quick Call",
      content: "5min coaching",
      type: "call",
      color: "#8B5CF6",
      delay: 2000,
      position: { bottom: "10%", right: "-25%", rotate: -10 },
      size: "medium",
    },

    // ZONE 1 - INSIDE PHONE (2 elements) - Positioned more to the right with proper sizing
    {
      icon: Zap,
      title: "Smart Nudge",
      content: "Take a 5min walk",
      type: "nudge",
      color: "#F59E0B",
      delay: 1800,
      position: { top: "35%", right: "8%", rotate: 8 },
      size: "small",
    },
    {
      icon: Target,
      title: "Goal Check",
      content: "On track!",
      type: "check",
      color: "#84CC16",
      delay: 2600,
      position: { bottom: "25%", right: "5%", rotate: -12 },
      size: "small",
    },
  ];

  const handleWatchDemo = () => {
    setShowVideoModal(true);
  };

  const handleCloseModal = () => {
    setShowVideoModal(false);
  };

  return (
    <section className="relative min-h-screen flex items-center bg-background overflow-hidden">
      {/* Mesh gradient background */}
      <div className="absolute inset-0 bg-gradient-mesh"></div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cpattern id='grid' width='40' height='40' patternUnits='userSpaceOnUse'%3e%3cpath d='m 40 0 l 0 40 m -40 0 l 40 0' fill='none' stroke='%23DCFF02' stroke-width='0.5'/%3e%3c/pattern%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='url(%23grid)' /%3e%3c/svg%3e")`,
        }}
      ></div>

      <div className="container mx-auto px-4 md:px-8 xl:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Content */}
          <div className="space-y-12">
            <div className="space-y-8 animate-slide-up">
              <div className="inline-block">
                <span className="text-primary text-sm font-bold tracking-wider uppercase border-2 border-primary px-4 py-2">
                  Life Improvement Platform
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl xl:text-8xl font-extrabold md:font-black leading-[0.9] tracking-tighter text-foreground">
                Stop guessing, start{" "}
                <span className="text-primary">optimizing</span>
              </h1>

              <p className="text-lg md:text-xl xl:text-2xl text-muted-foreground leading-relaxed max-w-lg">
                Score every area of your life. Get personalized actions to
                improve what matters most.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6">
              <Button
                variant="brutal"
                size="lg"
                className="group text-black hover:text-black"
                onClick={() => navigate("/life-score")}
              >
                Get your LIFE score
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>

              <Button variant="ghost" size="lg" onClick={handleWatchDemo}>
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Right side - Phone with Better Spaced Popouts */}
          <div className="relative lg:block hidden">
            <div className="relative w-full max-w-sm mx-auto">
              {/* Phone Frame */}
              <div
                className={`relative bg-card border-4 border-border transition-all duration-1000 ${
                  animationStarted
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{
                  borderRadius: "3rem",
                  aspectRatio: "9/19.5",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                }}
              >
                {/* Screen Content */}
                <div
                  className="absolute inset-4 bg-background overflow-hidden"
                  style={{ borderRadius: "2.5rem" }}
                >
                  {/* Status Bar with Current Time */}
                  <div className="flex justify-between items-center p-4 text-xs text-muted-foreground">
                    <span>{currentTime || "12:34"}</span>
                    <div className="flex gap-1">
                      <div className="w-4 h-2 bg-primary rounded-sm"></div>
                      <div className="w-6 h-2 bg-muted rounded-sm"></div>
                    </div>
                  </div>

                  {/* Screen Content with 1440 Logo */}
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center space-y-6 opacity-50">
                      {/* 1440 Logo */}
                      <div className="w-32 h-16 mx-auto flex items-center justify-center">
                        <img
                          src="/images/logo-white.png"
                          alt="1440 Logo"
                          className="max-w-full max-h-full object-contain opacity-60"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="h-3 bg-muted rounded w-32 mx-auto"></div>
                        <div className="h-3 bg-muted rounded w-24 mx-auto"></div>
                        <div className="h-2 bg-muted rounded w-20 mx-auto"></div>
                      </div>
                    </div>
                  </div>

                  {/* Home Indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-muted rounded-full"></div>
                </div>
              </div>

              {/* Better Spaced Popout Elements */}
              {popoutElements.map((element, index) => {
                const IconComponent = element.icon;
                const sizeClasses = {
                  small: "w-32 h-24 text-xs min-w-32",
                  medium: "w-36 h-22 text-sm min-w-36",
                  large: "w-44 h-26 text-sm min-w-44",
                };
                const iconSizes = {
                  small: "w-4 h-4",
                  medium: "w-5 h-5",
                  large: "w-6 h-6",
                };

                return (
                  <div
                    key={index}
                    className={`absolute transition-all duration-1000 delay-${
                      element.delay
                    } ${
                      animationStarted
                        ? "opacity-100 translate-y-0 scale-100"
                        : "opacity-0 translate-y-8 scale-90"
                    }`}
                    style={{
                      ...element.position,
                      transform: `rotate(${element.position.rotate}deg)`,
                      zIndex: 10 + index,
                    }}
                  >
                    <div className="relative group cursor-pointer">
                      {/* Main Popout Card - Properly Sized */}
                      <div
                        className={`${
                          sizeClasses[element.size]
                        } bg-background border-2 border-border p-4 shadow-xl transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl flex flex-col`}
                        style={{
                          boxShadow: `0 12px 35px ${element.color}30, 0 6px 15px rgba(0,0,0,0.15)`,
                          borderRadius: "16px",
                        }}
                      >
                        {/* Header with Icon */}
                        <div className="flex items-center gap-3 mb-2 flex-shrink-0">
                          <div
                            className="p-2 rounded-lg flex-shrink-0"
                            style={{ backgroundColor: `${element.color}20` }}
                          >
                            <IconComponent
                              className={`${iconSizes[element.size]}`}
                              style={{ color: element.color }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-foreground leading-tight text-xs">
                              {element.title}
                            </div>
                          </div>
                        </div>

                        {/* Content - Proper Space */}
                        <div className="text-muted-foreground font-medium leading-tight flex-1 text-xs">
                          {element.content}
                        </div>

                        {/* Type Badge */}
                        <div className="absolute -top-2 -right-2">
                          <div
                            className="w-3 h-3 rounded-full border-2 border-background"
                            style={{ backgroundColor: element.color }}
                          />
                        </div>
                      </div>

                      {/* Connection Line to Phone - More Visible */}
                      <div
                        className="absolute opacity-30 group-hover:opacity-60 transition-opacity duration-300"
                        style={{
                          width: "3px",
                          height: "60px",
                          background: `linear-gradient(to bottom, ${element.color}, ${element.color}50, transparent)`,
                          left: "50%",
                          top: element.position.top ? "auto" : "-60px",
                          bottom: element.position.bottom ? "auto" : "-60px",
                          transform: "translateX(-50%)",
                          borderRadius: "2px",
                        }}
                      />

                      {/* Glowing Effect */}
                      <div
                        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none"
                        style={{
                          background: `radial-gradient(circle at center, ${element.color}40, transparent 70%)`,
                          filter: "blur(8px)",
                        }}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Subtle Connection Network */}
              <div
                className={`absolute inset-0 pointer-events-none transition-opacity duration-1000 delay-3000 ${
                  animationStarted ? "opacity-30" : "opacity-0"
                }`}
              >
                <svg className="w-full h-full" viewBox="0 0 400 600">
                  {/* Connection lines between elements */}
                  <path
                    d="M100,80 Q200,60 300,120"
                    stroke="hsl(var(--ml-primary))"
                    strokeWidth="1"
                    fill="none"
                    strokeDasharray="3,6"
                    className="animate-pulse"
                    opacity="0.4"
                  />
                  <path
                    d="M320,180 Q250,200 120,280"
                    stroke="hsl(var(--ml-primary))"
                    strokeWidth="1"
                    fill="none"
                    strokeDasharray="3,6"
                    className="animate-pulse"
                    style={{ animationDelay: "1s" }}
                    opacity="0.3"
                  />
                  <path
                    d="M100,400 Q200,380 300,440"
                    stroke="hsl(var(--ml-primary))"
                    strokeWidth="1"
                    fill="none"
                    strokeDasharray="3,6"
                    className="animate-pulse"
                    style={{ animationDelay: "0.5s" }}
                    opacity="0.2"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal Overlay */}
      {showVideoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Dark overlay background */}
          <div
            className="absolute inset-0 bg-black bg-opacity-80 backdrop-blur-sm"
            onClick={handleCloseModal}
          ></div>

          {/* Modal content */}
          <div className="relative z-10 w-full max-w-4xl mx-4">
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Video container */}
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                src="https://player.vimeo.com/video/895330870?badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                }}
                title="1440_V17_5"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
